import {
  type Announcements,
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  type DropAnimation,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  type UniqueIdentifier,
  closestCenter,
  defaultDropAnimation,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { useConfirmationStore } from '@/components/Confirmation/ConfirmationStore';
import { useUpdateGroupCollapsed } from '@/hooks/useUpdateGroupCollapsed';
import { CSS } from '@dnd-kit/utilities';
import { VList } from 'virtua';
import { SortableTreeItem } from './SortableTreeItem';
import type { FlattenedItem, TreeItems } from './types';
import {
  buildTree,
  flattenTree,
  getChildCount,
  getProjection,
  removeChildrenOf,
  setProperty,
} from './utilities';
const measuring = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

const dropAnimationConfig: DropAnimation = {
  keyframes({ transform }) {
    return [
      { opacity: 1, transform: CSS.Transform.toString(transform.initial) },
      {
        opacity: 0,
        transform: CSS.Transform.toString({
          ...transform.final,
          x: transform.final.x + 5,
          y: transform.final.y + 5,
        }),
      },
    ];
  },
  easing: 'ease-out',
  sideEffects({ active }) {
    active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing,
    });
  },
};

interface Props {
  collapsible?: boolean;
  value: TreeItems;
  onValueChange: (value: TreeItems) => void;
  indentationWidth?: number;
}

export function SortableTree({
  value,
  onValueChange,
  indentationWidth = 16,
}: Props) {
  const [items, setItems] = useState(value);

  useEffect(() => {
    setItems(value);
  }, [value]);

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const [currentPosition, setCurrentPosition] = useState<{
    parentId: UniqueIdentifier | null;
    overId: UniqueIdentifier;
  } | null>(null);

  const flattenedItems = useMemo(() => {
    const flattenedTree = flattenTree(items);

    const collapsedItems = flattenedTree.reduce(
      (prev, { children, collapsed, id }) => {
        // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
        if (collapsed && children.length) return [...prev, id];

        return prev;
      },
      [] as UniqueIdentifier[],
    );

    const removalItems = activeId
      ? [activeId, ...collapsedItems]
      : collapsedItems;

    return removeChildrenOf(flattenedTree, removalItems);
  }, [activeId, items]);

  const projected =
    activeId && overId
      ? getProjection(
          flattenedItems,
          activeId,
          overId,
          offsetLeft,
          indentationWidth,
        )
      : null;

  const { groupCollapsedMutate } = useUpdateGroupCollapsed();
  const confirm = useConfirmationStore((state) => state.openConfirmation);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 4,
      },
    }),

    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
  );

  const sortedIds = useMemo(
    () => flattenedItems.map(({ id }) => id),
    [flattenedItems],
  );
  const activeItem = activeId
    ? flattenedItems.find(({ id }) => id === activeId)
    : null;

  const announcements: Announcements = {
    onDragStart({ active }) {
      return `Picked up ${active.id}.`;
    },
    onDragMove({ active, over }) {
      return getMovementAnnouncement('onDragMove', active.id, over?.id);
    },
    onDragOver({ active, over }) {
      return getMovementAnnouncement('onDragOver', active.id, over?.id);
    },
    onDragEnd({ active, over }) {
      return getMovementAnnouncement('onDragEnd', active.id, over?.id);
    },
    onDragCancel({ active }) {
      return `Moving was cancelled. ${active.id} was dropped in its original position.`;
    },
  };

  return (
    <DndContext
      accessibility={{ announcements }}
      sensors={sensors}
      collisionDetection={closestCenter}
      measuring={measuring}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
        <VList className="scrollbar-hide h-full " overscan={8}>
          {flattenedItems.map((tree) => (
            <SortableTreeItem
              key={tree.id}
              tree={tree}
              childCount={getChildCount(items, tree.id)}
              projectedParentId={projected?.parentId}
              depth={
                tree.id === activeId && projected ? projected.depth : tree.depth
              }
              indentationWidth={indentationWidth}
              collapsed={Boolean(tree.collapsed)}
              onCollapse={() => handleCollapse(tree.id)}
            />
          ))}
        </VList>
      </SortableContext>
      {typeof window !== 'undefined' &&
        createPortal(
          <DragOverlay dropAnimation={dropAnimationConfig}>
            {activeId && activeItem && activeItem.type !== 'WORKSPACE' ? (
              <SortableTreeItem
                tree={activeItem}
                depth={activeItem.depth}
                clone
                childCount={getChildCount(items, activeId)}
                indentationWidth={indentationWidth}
              />
            ) : null}
          </DragOverlay>,
          document.body,
        )}
    </DndContext>
  );

  function handleDragStart({ active: { id: activeId } }: DragStartEvent) {
    setActiveId(activeId);
    setOverId(activeId);

    const activeItem = flattenedItems.find(({ id }) => id === activeId);

    if (activeItem) {
      setCurrentPosition({
        parentId: activeItem.parentId,
        overId: activeId,
      });
    }

    document.body.style.setProperty('cursor', 'grabbing');
  }

  function handleDragMove({ delta }: DragMoveEvent) {
    setOffsetLeft(delta.x);
  }

  function handleDragOver({ over }: DragOverEvent) {
    setOverId(over?.id ?? null);
  }

  function wouldCreateCycle(
    items: FlattenedItem[],
    sourceId: UniqueIdentifier,
    targetParentId: UniqueIdentifier | null,
  ): boolean {
    if (targetParentId === null) return false;
    if (sourceId === targetParentId) return true;

    let currentParentId = targetParentId;
    const visited = new Set<UniqueIdentifier>();

    while (currentParentId) {
      if (visited.has(currentParentId)) return true;
      if (currentParentId === sourceId) return true;

      visited.add(currentParentId);
      const parentItem = items.find((item) => item.id === currentParentId);
      currentParentId = (parentItem?.parentId ?? null) as UniqueIdentifier;
    }

    return false;
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    resetState();

    if (projected && over) {
      const { depth, parentId, projectedTeamId } = projected;
      const clonedItems: FlattenedItem[] = JSON.parse(
        JSON.stringify(flattenTree(items)),
      );

      if (wouldCreateCycle(clonedItems, active.id, parentId)) {
        return;
      }

      const overIndex = clonedItems.findIndex(({ id }) => id === over.id);
      const activeIndex = clonedItems.findIndex(({ id }) => id === active.id);
      const activeTreeItem = clonedItems[activeIndex];

      // Check if we're moving between team and private folders
      const currentTeamId = activeTreeItem?.teamId;

      const isMovingFromTeamToPrivate = currentTeamId && !projectedTeamId;
      const isMovingFromPrivateToTeam = !currentTeamId && projectedTeamId;

      const performMove = () => {
        if (activeTreeItem)
          clonedItems[activeIndex] = {
            ...activeTreeItem,
            depth,
            parentId,
            teamId: projectedTeamId,
          };

        const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);
        const newItems = buildTree(sortedItems);

        onValueChange(newItems);
        setItems(newItems);
      };

      if (isMovingFromTeamToPrivate) {
        confirm({
          title: 'Make this folder private?',
          description:
            'Only you will be able to access and edit the contents of this folder and all nested subfolders.',
          actionLabel: 'Confirm',
          onAction: performMove,
          cancelLabel: 'Cancel',
          onCancel: () => {},
        });
      } else if (isMovingFromPrivateToTeam) {
        confirm({
          title: 'Share folder with team?',
          description:
            'Anyone in your team will be able to access and edit the contents of this folder and all nested subfolders.',
          actionLabel: 'Confirm',
          onAction: performMove,
          cancelLabel: 'Cancel',
          onCancel: () => {},
        });
      } else {
        performMove();
      }
    }
  }

  function handleDragCancel() {
    resetState();
  }

  function resetState() {
    setOverId(null);
    setActiveId(null);
    setOffsetLeft(0);
    setCurrentPosition(null);

    document.body.style.setProperty('cursor', '');
  }

  function handleCollapse(id: UniqueIdentifier) {
    setItems((items) =>
      setProperty(items, id, 'collapsed', (value) => {
        const newCollapsedValue = !value;
        groupCollapsedMutate({
          id: id as string,
          collapsed: newCollapsedValue,
        });

        return newCollapsedValue;
      }),
    );
  }

  function getMovementAnnouncement(
    eventName: string,
    activeId: UniqueIdentifier,
    overId?: UniqueIdentifier,
  ) {
    if (overId && projected) {
      if (eventName !== 'onDragEnd') {
        if (
          currentPosition &&
          projected.parentId === currentPosition.parentId &&
          overId === currentPosition.overId
        ) {
          return;
        }
        setCurrentPosition({
          parentId: projected.parentId,
          overId,
        });
      }

      const clonedItems: FlattenedItem[] = JSON.parse(
        JSON.stringify(flattenTree(items)),
      );
      const overIndex = clonedItems.findIndex(({ id }) => id === overId);
      const activeIndex = clonedItems.findIndex(({ id }) => id === activeId);
      const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);

      const previousItem = sortedItems[overIndex - 1];

      let announcement = '';
      const movedVerb = eventName === 'onDragEnd' ? 'dropped' : 'moved';
      const nestedVerb = eventName === 'onDragEnd' ? 'dropped' : 'nested';

      if (!previousItem) {
        const nextItem = sortedItems[overIndex + 1];
        announcement = `${activeId} was ${movedVerb} before ${nextItem?.id}.`;
      } else {
        if (projected.depth > previousItem.depth) {
          announcement = `${activeId} was ${nestedVerb} under ${previousItem.id}.`;
        } else {
          let previousSibling: FlattenedItem | undefined = previousItem;
          while (previousSibling && projected.depth < previousSibling.depth) {
            const parentId: UniqueIdentifier | null = previousSibling.parentId;
            previousSibling = sortedItems.find(({ id }) => id === parentId);
          }

          if (previousSibling) {
            announcement = `${activeId} was ${movedVerb} after ${previousSibling.id}.`;
          }
        }
      }

      return announcement;
    }

    return;
  }
}
